import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ArrowLeft, Send, RotateCcw, Users, X, Download, History, Sparkles, GitCompare, Pencil, Save, LogOut, ListChecks, Plus, Trash2, Check, AlertTriangle, Bell, NotebookPen, Share2, Copy } from "lucide-react";

axios.defaults.withCredentials = true;

// ---------- Brand ----------
const NAVY = "#000000";
const ROSE = "#666666";
const CREAM = "#FFFFFF";
const PAPER = "#FFFFFF";
const HAIR = "#000000";
const DISPLAY = "'Archivo Black', 'Inter', system-ui, sans-serif";
const BODY = "'Inter', system-ui, -apple-system, sans-serif";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Avatar ----------
function Avatar({ member, size = 52 }) {
  const isExec = member.group === "Executive Suite" || member.group === "Founder's Office";
  const initials = member.initials || (member.role || "")
    .replace(/^Chief /, "")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
  return (
    <div
      data-testid={`avatar-${member.id}`}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: isExec ? NAVY : ROSE,
        color: CREAM,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: DISPLAY,
        fontSize: size * 0.34,
        letterSpacing: "0.02em",
        flexShrink: 0,
        boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
      }}
    >
      {initials}
    </div>
  );
}

// ---------- Markdown export helpers ----------
function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function conversationToMarkdown(member, messages) {
  const lines = [
    `# ${member.role}`,
    `_${member.role} · channeling ${member.inspired_by || "—"}_`,
    `_The Executive_`,
    "",
  ];
  messages.forEach((m) => {
    lines.push(m.role === "user" ? "**Teresa**" : `**The ${member.role}**`);
    lines.push("");
    lines.push(m.content);
    lines.push("");
    lines.push("---");
    lines.push("");
  });
  return lines.join("\n");
}

function boardroomToMarkdown(session, teamById) {
  const lines = [
    `# Boardroom — ${new Date(session.created_at).toLocaleString()}`,
    "",
    `**Question:** ${session.question}`,
    "",
  ];
  if (session.memo && session.memo.text) {
    lines.push(`## Decision Memo — ${session.memo.author_name}`);
    lines.push("");
    lines.push(session.memo.text);
    lines.push("");
    lines.push("---");
    lines.push("");
  }
  session.responses.forEach((r) => {
    const m = teamById[r.member_id] || { role: r.role };
    lines.push(`## The ${m.role}`);
    lines.push("");
    lines.push(r.text);
    lines.push("");
  });
  return lines.join("\n");
}

// ---------- Reminders Bell ----------
function BellMenu({ reminders, sprint, open, onToggle, onOpenPlan }) {
  const total = (reminders?.overdue || 0) + (reminders?.due_today || 0);
  const overdueList = sprint?.overdue || [];
  const thisWeekList = sprint?.this_week || [];
  return (
    <div
      data-testid="bell-menu"
      style={{ position: "relative", display: "inline-flex", marginRight: 10 }}
    >
      <button
        data-testid="bell-toggle"
        onClick={onToggle}
        aria-label="Reminders"
        style={{
          border: "2px solid #000000",
          background: total > 0 ? "#000000" : "#FFFFFF",
          color: total > 0 ? "#FFFFFF" : "#000000",
          cursor: "pointer",
          padding: "8px 12px",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontFamily: "'Inter', system-ui, sans-serif",
          fontWeight: 800,
          fontSize: 12,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          transition: "transform .12s ease, box-shadow .12s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translate(-2px,-2px)";
          e.currentTarget.style.boxShadow = "4px 4px 0 #000000";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "none";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <Bell size={14} />
        {total > 0 && <span>{total}</span>}
      </button>

      {open && (
        <div
          data-testid="bell-dropdown"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            zIndex: 50,
            background: "#FFFFFF",
            border: "2px solid #000000",
            width: 340,
            maxHeight: 460,
            overflowY: "auto",
            boxShadow: "6px 6px 0 #000000",
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "2px solid #000000",
              fontFamily: "'Archivo Black', sans-serif",
              fontSize: 14,
              textTransform: "uppercase",
              letterSpacing: "-0.01em",
            }}
          >
            Reminders
          </div>

          {overdueList.length === 0 && thisWeekList.length === 0 && (
            <div style={{ padding: "20px 18px", fontSize: 13, color: "#666666", fontWeight: 600 }}>
              Nothing on the radar. Convene the room to set milestones.
            </div>
          )}

          {overdueList.length > 0 && (
            <div>
              <div
                style={{
                  padding: "12px 18px 6px",
                  fontSize: 10,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  fontWeight: 800,
                  color: "#000000",
                }}
              >
                Overdue
              </div>
              {overdueList.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: "8px 18px 10px",
                    fontSize: 13,
                    fontWeight: 600,
                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                  }}
                >
                  <div style={{ color: "#000000" }}>{m.title}</div>
                  <div style={{ fontSize: 11, color: "#666666", marginTop: 2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Due {m.due_date}
                  </div>
                </div>
              ))}
            </div>
          )}

          {thisWeekList.length > 0 && (
            <div>
              <div
                style={{
                  padding: "12px 18px 6px",
                  fontSize: 10,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  fontWeight: 800,
                  color: "#000000",
                }}
              >
                This week
              </div>
              {thisWeekList.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: "8px 18px 10px",
                    fontSize: 13,
                    fontWeight: 600,
                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                  }}
                >
                  <div style={{ color: "#000000" }}>{m.title}</div>
                  <div style={{ fontSize: 11, color: "#666666", marginTop: 2, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Due {m.due_date}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            data-testid="bell-open-plan"
            onClick={() => {
              onToggle();
              onOpenPlan();
            }}
            style={{
              width: "100%",
              padding: "14px 18px",
              background: "#000000",
              color: "#FFFFFF",
              border: "none",
              cursor: "pointer",
              fontFamily: "'Inter', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            Open action plan →
          </button>
        </div>
      )}
    </div>
  );
}



// ---------- App ----------
// ---------- App ----------
function RoomBadge({ boardName, userName, onLogout, onManageBilling }) {
  if (!boardName && !userName) return null;
  return (
    <div
      data-testid="room-badge"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px",
        borderRadius: 0,
        border: `1px solid ${HAIR}`,
        background: PAPER,
        fontSize: 11.5,
        color: "rgba(0,0,0,0.75)",
        fontFamily: BODY,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        maxWidth: 320,
        overflow: "hidden",
      }}
      title={userName ? `${userName} · ${boardName}` : boardName}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: ROSE,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: DISPLAY,
        }}
      >
        {boardName || "—"}
      </span>
      {userName && (
        <>
          <span style={{ opacity: 0.4 }}>·</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{userName}</span>
        </>
      )}
      {onManageBilling && (
        <button
          data-testid="manage-billing-btn"
          onClick={onManageBilling}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "rgba(0,0,0,0.55)",
            padding: 2,
            marginLeft: 2,
            fontFamily: BODY,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
          aria-label="Manage billing"
          title="Manage billing"
        >
          Billing
        </button>
      )}
      {onLogout && (
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "rgba(0,0,0,0.55)",
            padding: 2,
            marginLeft: 2,
            display: "flex",
          }}
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut size={12} />
        </button>
      )}
    </div>
  );
}

export default function EverAfterCabinet({ user, onLogout }) {
  const [view, setView] = useState("roster"); // roster | chat | boardroom | history
  const [activeId, setActiveId] = useState(null);
  const [team, setTeam] = useState([]);
  const [groups, setGroups] = useState([]);
  const [defaultPanel, setDefaultPanel] = useState([]);
  const [bootError, setBootError] = useState("");

  // 1:1 state
  const [conversations, setConversations] = useState({}); // memberId -> messages[]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Founder notes (per-advisor standing context)
  const [notesByMember, setNotesByMember] = useState({}); // memberId -> string
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  // Roster filter
  const [filterGroup, setFilterGroup] = useState("All");

  // Compare state
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [compareQuestion, setCompareQuestion] = useState("");
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareResult, setCompareResult] = useState(null); // {question, a:{...}, b:{...}}

  // Weekly briefing state
  const [briefing, setBriefing] = useState(null); // {id, text, created_at, author_name, author_inspired_by}
  const [briefingLoading, setBriefingLoading] = useState(false);

  // Action plan state
  const [plan, setPlan] = useState(null); // {milestones:[...], reminders:{...}}
  const [reminders, setReminders] = useState({ overdue: 0, due_today: 0, due_this_week: 0, open: 0, total: 0 });
  const [newMilestone, setNewMilestone] = useState({ title: "", owner_role: "", due_date: "" });
  const [milestoneSaving, setMilestoneSaving] = useState(false);
  const [extractingFromMemo, setExtractingFromMemo] = useState(false);

  // Share-plan modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Reminders bell state
  const [bellOpen, setBellOpen] = useState(false);

  // Founder Notes (multi-titled, separate from per-advisor notes)
  const [founderNotes, setFounderNotes] = useState([]); // [{id, title, body, updated_at}]
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({ title: "", body: "" });
  const [noteSaving, setNoteSaving] = useState(false);

  // Settings / board naming
  const [boardName, setBoardName] = useState("");
  const [userName, setUserName] = useState("");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [onboardDraft, setOnboardDraft] = useState({ board: "", user: "" });
  const [onboardSaving, setOnboardSaving] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");

  // boardroom state
  const [panel, setPanel] = useState([]);
  const [boardQuestion, setBoardQuestion] = useState("");
  const [boardResponses, setBoardResponses] = useState({}); // id -> {status, text}
  const [boardConvening, setBoardConvening] = useState(false);
  const [boardSessions, setBoardSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [memo, setMemo] = useState(null); // {text, author_name, created_at}
  const [memoLoading, setMemoLoading] = useState(false);

  const scrollRef = useRef(null);
  const teamById = useMemo(() => Object.fromEntries(team.map((m) => [m.id, m])), [team]);
  const active = activeId ? teamById[activeId] : null;
  const convo = (activeId && conversations[activeId]) || [];

  // Memoized derived plan splits — must live at top of component so hook order
  // stays stable across the many `if (view === ...) return ...` branches below.
  const planMilestones = (plan && plan.milestones) || [];
  const openMilestones = useMemo(
    () => planMilestones.filter((m) => m.status === "open"),
    [planMilestones]
  );
  const doneMilestones = useMemo(
    () => planMilestones.filter((m) => m.status === "done"),
    [planMilestones]
  );

  // Boot: load roster
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [teamRes, settingsRes, planRes] = await Promise.all([
          axios.get(`${API}/team`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/plan`),
        ]);
        if (!alive) return;
        setTeam(teamRes.data.members);
        setGroups(teamRes.data.groups);
        setDefaultPanel(teamRes.data.default_panel);
        setPanel(teamRes.data.default_panel);
        setBoardName(settingsRes.data.board_name || "");
        setUserName(settingsRes.data.user_name || "");
        setPlan(planRes.data);
        setReminders(planRes.data.reminders || reminders);
        if (planRes.data.share_token) setShareToken(planRes.data.share_token);
        setSettingsLoaded(true);
      } catch (e) {
        setBootError("Couldn't reach the team. Refresh and try again.");
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openPlan() {
    setView("plan");
    try {
      const { data } = await axios.get(`${API}/plan`);
      setPlan(data);
      setReminders(data.reminders || reminders);
    } catch (e) {
      console.warn("openPlan failed", e);
    }
  }

  async function addMilestone() {
    const t = newMilestone.title.trim();
    if (!t || milestoneSaving) return;
    setMilestoneSaving(true);
    try {
      const { data } = await axios.post(`${API}/plan/milestone`, {
        title: t,
        owner_role: newMilestone.owner_role || null,
        due_date: newMilestone.due_date || null,
      });
      setPlan((p) => ({
        ...(p || { milestones: [] }),
        milestones: [...((p && p.milestones) || []), data.milestone],
      }));
      setReminders(data.reminders);
      setNewMilestone({ title: "", owner_role: "", due_date: "" });
    } catch (e) {
      console.warn("addMilestone failed", e);
    } finally {
      setMilestoneSaving(false);
    }
  }

  async function setMilestoneStatus(id, status) {
    try {
      const { data } = await axios.patch(`${API}/plan/milestone/${id}`, { status });
      setPlan((p) => ({
        ...p,
        milestones: p.milestones.map((m) => (m.id === id ? data.milestone : m)),
      }));
      setReminders(data.reminders);
    } catch (e) {
      console.warn("setMilestoneStatus failed", e);
    }
  }

  async function deleteMilestone(id) {
    try {
      const { data } = await axios.delete(`${API}/plan/milestone/${id}`);
      setPlan((p) => ({ ...p, milestones: p.milestones.filter((m) => m.id !== id) }));
      setReminders(data.reminders);
    } catch (e) {
      console.warn("deleteMilestone failed", e);
    }
  }

  // -------- Share Plan --------
  async function openShareModal() {
    setShareModalOpen(true);
    setShareCopied(false);
    if (shareToken) return;
    setShareBusy(true);
    try {
      const { data } = await axios.post(`${API}/plan/share`);
      setShareToken(data.share_token);
    } catch (e) {
      console.warn("plan/share failed", e);
    } finally {
      setShareBusy(false);
    }
  }

  async function regenerateShareToken() {
    setShareBusy(true);
    setShareCopied(false);
    try {
      const { data } = await axios.post(`${API}/plan/share`);
      setShareToken(data.share_token);
    } catch (e) {
      console.warn("regenerate share failed", e);
    } finally {
      setShareBusy(false);
    }
  }

  async function revokeShareToken() {
    setShareBusy(true);
    try {
      await axios.delete(`${API}/plan/share`);
      setShareToken(null);
      setShareCopied(false);
    } catch (e) {
      console.warn("revoke share failed", e);
    } finally {
      setShareBusy(false);
    }
  }

  function shareUrlFor(token) {
    if (!token) return "";
    return `${window.location.origin}/share/${token}`;
  }

  async function copyShareLink() {
    if (!shareToken) return;
    try {
      await navigator.clipboard.writeText(shareUrlFor(shareToken));
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (e) {
      console.warn("clipboard write failed", e);
    }
  }

  // -------- Founder Notes (multi-titled) --------
  async function openNotes() {
    setView("notes");
    try {
      const { data } = await axios.get(`${API}/notes`);
      setFounderNotes(data.notes || []);
      if (data.notes && data.notes.length > 0) {
        const first = data.notes[0];
        setActiveNoteId(first.id);
        setNoteDraft({ title: first.title, body: first.body });
      } else {
        setActiveNoteId(null);
        setNoteDraft({ title: "", body: "" });
      }
    } catch (e) {
      console.warn("openNotes failed", e);
    }
  }

  async function createNote() {
    try {
      const { data } = await axios.post(`${API}/notes`, {
        title: "Untitled note",
        body: "",
      });
      setFounderNotes((n) => [data, ...n]);
      setActiveNoteId(data.id);
      setNoteDraft({ title: data.title, body: data.body });
    } catch (e) {
      console.warn("createNote failed", e);
    }
  }

  function selectNote(id) {
    const n = founderNotes.find((x) => x.id === id);
    if (!n) return;
    setActiveNoteId(id);
    setNoteDraft({ title: n.title, body: n.body });
  }

  async function saveActiveNote() {
    if (!activeNoteId || noteSaving) return;
    setNoteSaving(true);
    try {
      const { data } = await axios.patch(`${API}/notes/${activeNoteId}`, {
        title: noteDraft.title,
        body: noteDraft.body,
      });
      setFounderNotes((n) =>
        [data, ...n.filter((x) => x.id !== activeNoteId)]
      );
    } catch (e) {
      console.warn("saveNote failed", e);
    } finally {
      setNoteSaving(false);
    }
  }

  async function deleteActiveNote() {
    if (!activeNoteId) return;
    const id = activeNoteId;
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/notes/${id}`);
      const remaining = founderNotes.filter((x) => x.id !== id);
      setFounderNotes(remaining);
      if (remaining.length > 0) {
        setActiveNoteId(remaining[0].id);
        setNoteDraft({ title: remaining[0].title, body: remaining[0].body });
      } else {
        setActiveNoteId(null);
        setNoteDraft({ title: "", body: "" });
      }
    } catch (e) {
      console.warn("deleteNote failed", e);
    }
  }


  async function extractFromMemo() {
    if (!memo || !activeSessionId || extractingFromMemo) return;
    setExtractingFromMemo(true);
    try {
      const { data } = await axios.post(`${API}/plan/extract`, {
        text: memo.text,
        source_kind: "memo",
        source_id: activeSessionId,
      });
      setReminders(data.reminders);
      // Toast-style minimal confirmation
      alert(`${data.added.length} milestone${data.added.length === 1 ? "" : "s"} added to your plan.`);
    } catch {
      alert("Couldn't extract milestones. Try again.");
    } finally {
      setExtractingFromMemo(false);
    }
  }

  async function submitOnboarding() {
    const bn = onboardDraft.board.trim();
    if (!bn || onboardSaving) return;
    setOnboardSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings`, {
        board_name: bn,
        user_name: onboardDraft.user.trim(),
      });
      setBoardName(data.board_name || "");
      setUserName(data.user_name || "");
    } catch (e) {
      console.warn("submitOnboarding failed", e);
    } finally {
      setOnboardSaving(false);
    }
  }

  async function openBillingPortal() {
    try {
      const { data } = await axios.post(`${API}/billing/portal`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't open billing portal.";
      window.alert(detail);
    }
  }

  async function saveBoardRename() {
    const bn = renameDraft.trim();
    if (!bn) return;
    try {
      const { data } = await axios.put(`${API}/settings`, { board_name: bn });
      setBoardName(data.board_name || "");
      setRenaming(false);
    } catch (e) {
      console.warn("saveBoardRename failed", e);
    }
  }

  // Auto-scroll chat
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [convo, loading, view]);

  async function openChat(memberId) {
    setActiveId(memberId);
    setView("chat");
    setNotesEditing(false);
    if (!conversations[memberId]) {
      try {
        const { data } = await axios.get(`${API}/chat/${memberId}`);
        setConversations((c) => ({ ...c, [memberId]: data.messages || [] }));
      } catch {
        setConversations((c) => ({ ...c, [memberId]: [] }));
      }
    }
    if (notesByMember[memberId] === undefined) {
      try {
        const { data } = await axios.get(`${API}/notes/${memberId}`);
        setNotesByMember((n) => ({ ...n, [memberId]: data.notes || "" }));
      } catch {
        setNotesByMember((n) => ({ ...n, [memberId]: "" }));
      }
    }
  }

  function startEditingNotes() {
    setNotesDraft(notesByMember[activeId] || "");
    setNotesEditing(true);
  }

  async function saveNotes() {
    if (!activeId || notesSaving) return;
    setNotesSaving(true);
    try {
      const { data } = await axios.put(`${API}/notes/${activeId}`, { notes: notesDraft });
      setNotesByMember((n) => ({ ...n, [activeId]: data.notes || "" }));
      setNotesEditing(false);
    } catch (e) {
      console.warn("saveNotes failed", e);
    } finally {
      setNotesSaving(false);
    }
  }

  async function runCompare() {
    const q = compareQuestion.trim();
    if (!q || !compareA || !compareB || compareA === compareB || compareLoading) return;
    setCompareLoading(true);
    setCompareResult({ loading: true, question: q });
    try {
      const { data } = await axios.post(`${API}/compare`, {
        question: q,
        a_id: compareA,
        b_id: compareB,
      });
      setCompareResult(data);
    } catch {
      setCompareResult({ error: true, question: q });
    } finally {
      setCompareLoading(false);
    }
  }

  async function openBriefing() {
    setView("briefing");
    if (briefing) return;
    try {
      const { data } = await axios.get(`${API}/briefing/latest`);
      if (data && data.id) setBriefing(data);
    } catch (e) {
      console.warn("openBriefing failed", e);
    }
  }

  async function generateBriefing() {
    if (briefingLoading) return;
    setBriefingLoading(true);
    try {
      const { data } = await axios.post(`${API}/briefing`);
      setBriefing(data);
    } catch (e) {
      console.warn("generateBriefing failed", e);
    } finally {
      setBriefingLoading(false);
    }
  }

  async function send(text) {
    const content = (text ?? input).trim();
    if (!content || loading || !active) return;
    setInput("");
    // Optimistic user bubble
    const tentative = [...convo, { role: "user", content }];
    setConversations((c) => ({ ...c, [activeId]: tentative }));
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat/${activeId}`, { content });
      setConversations((c) => ({ ...c, [activeId]: data.messages }));
    } catch {
      setConversations((c) => ({
        ...c,
        [activeId]: [
          ...tentative,
          { role: "assistant", content: "I hit a connection error reaching the model. Give it another try." },
        ],
      }));
    } finally {
      setLoading(false);
    }
  }

  async function clearChat() {
    if (!activeId) return;
    try {
      await axios.delete(`${API}/chat/${activeId}`);
    } catch (e) {
      console.warn("clearChat failed", e);
    }
    setConversations((c) => ({ ...c, [activeId]: [] }));
  }

  async function convene() {
    const q = boardQuestion.trim();
    if (!q || boardConvening || panel.length === 0) return;
    setBoardConvening(true);
    setActiveSessionId(null);
    setMemo(null);
    const init = {};
    panel.forEach((id) => (init[id] = { status: "loading", text: "" }));
    setBoardResponses(init);
    try {
      const { data } = await axios.post(`${API}/boardroom`, { question: q, panel });
      const next = {};
      data.responses.forEach((r) => {
        next[r.member_id] = { status: r.status, text: r.text };
      });
      setBoardResponses(next);
      setActiveSessionId(data.id);
    } catch {
      const next = {};
      panel.forEach((id) => (next[id] = { status: "error", text: "Connection error." }));
      setBoardResponses(next);
    } finally {
      setBoardConvening(false);
    }
  }

  function togglePanel(id) {
    setPanel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  async function openHistory() {
    try {
      const { data } = await axios.get(`${API}/boardroom`);
      setBoardSessions(data.sessions || []);
    } catch {
      setBoardSessions([]);
    }
    setView("history");
  }

  async function loadSession(sessionId) {
    try {
      const { data } = await axios.get(`${API}/boardroom/${sessionId}`);
      setBoardQuestion(data.question);
      setPanel(data.panel);
      const next = {};
      data.responses.forEach((r) => {
        next[r.member_id] = { status: r.status, text: r.text };
      });
      setBoardResponses(next);
      setActiveSessionId(data.id);
      setMemo(data.memo || null);
      setView("boardroom");
    } catch (e) {
      console.warn("loadSession failed", e);
    }
  }

  async function synthesizeMemo() {
    if (!activeSessionId || memoLoading) return;
    setMemoLoading(true);
    try {
      const { data } = await axios.post(`${API}/boardroom/${activeSessionId}/memo`);
      setMemo(data.memo);
    } catch {
      setMemo({
        text: "Couldn't synthesize the memo. Try again.",
        author_name: "—",
        created_at: new Date().toISOString(),
        error: true,
      });
    } finally {
      setMemoLoading(false);
    }
  }

  const wrap = { minHeight: "100vh", background: CREAM, fontFamily: BODY, color: NAVY };

  // ========== Boot / error ==========
  if (bootError) {
    return (
      <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <p data-testid="boot-error" style={{ fontFamily: DISPLAY, fontStyle: "normal", color: ROSE }}>
          {bootError}
        </p>
      </div>
    );
  }
  if (team.length === 0 || !settingsLoaded) {
    return (
      <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((d) => (
            <span
              key={d}
              className="ea-dot"
              style={{ width: 8, height: 8, background: ROSE, animationDelay: `${d * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // ========== ONBOARDING ==========
  if (!boardName) {
    return (
      <div
        style={{
          ...wrap,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 22px",
        }}
        data-testid="onboarding-view"
      >
        <div style={{ maxWidth: 460, width: "100%" }}>
          <h1
            style={{
              fontFamily: DISPLAY,
              fontSize: 40,
              margin: "0 0 8px",
              fontWeight: 400,
              lineHeight: 1.05,
              textAlign: "center",
            }}
          >
            Name your board.
          </h1>
          <p
            style={{
              fontFamily: DISPLAY,
              fontStyle: "normal",
              fontSize: 16,
              color: "rgba(0,0,0,0.7)",
              textAlign: "center",
              marginTop: 0,
              marginBottom: 32,
              lineHeight: 1.5,
            }}
          >
            Sixteen advisors are briefed and waiting. Tell us what to call this room.
          </p>

          <label
            style={{
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: ROSE,
              fontWeight: 700,
              display: "block",
              marginBottom: 6,
            }}
          >
            Your name
          </label>
          <input
            data-testid="onboard-user-input"
            value={onboardDraft.user}
            onChange={(e) => setOnboardDraft((d) => ({ ...d, user: e.target.value }))}
            placeholder="Teresa"
            maxLength={80}
            style={{
              width: "100%",
              border: `1px solid ${HAIR}`,
              borderRadius: 12,
              padding: "12px 14px",
              fontFamily: BODY,
              fontSize: 15,
              color: NAVY,
              background: PAPER,
              outline: "none",
              marginBottom: 18,
              boxSizing: "border-box",
            }}
          />

          <label
            style={{
              fontSize: 11,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: ROSE,
              fontWeight: 700,
              display: "block",
              marginBottom: 6,
            }}
          >
            Board name
          </label>
          <input
            data-testid="onboard-board-input"
            value={onboardDraft.board}
            onChange={(e) => setOnboardDraft((d) => ({ ...d, board: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitOnboarding();
              }
            }}
            placeholder="e.g. The Executive, Teresa's Board, Operating Room"
            maxLength={120}
            autoFocus
            style={{
              width: "100%",
              border: `1px solid ${HAIR}`,
              borderRadius: 12,
              padding: "12px 14px",
              fontFamily: BODY,
              fontSize: 15,
              color: NAVY,
              background: PAPER,
              outline: "none",
              marginBottom: 22,
              boxSizing: "border-box",
            }}
          />

          <button
            data-testid="onboard-submit"
            onClick={submitOnboarding}
            disabled={!onboardDraft.board.trim() || onboardSaving}
            style={{
              width: "100%",
              border: "none",
              cursor: onboardDraft.board.trim() && !onboardSaving ? "pointer" : "default",
              background:
                onboardDraft.board.trim() && !onboardSaving ? NAVY : "rgba(0,0,0,0.4)",
              color: CREAM,
              borderRadius: 12,
              padding: "13px 18px",
              fontFamily: DISPLAY,
              fontSize: 16,
              letterSpacing: "0.02em",
            }}
          >
            {onboardSaving ? "Setting up…" : "Enter the room"}
          </button>
        </div>
      </div>
    );
  }

  // ========== ROSTER ==========
  if (view === "roster") {
    return (
      <div style={wrap} data-testid="roster-view">
        <div
          style={{
            position: "absolute",
            top: 18,
            right: 22,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
          }}
        >
          <BellMenu
            reminders={reminders}
            sprint={plan?.sprint}
            open={bellOpen}
            onToggle={() => setBellOpen((b) => !b)}
            onOpenPlan={openPlan}
          />
          <RoomBadge boardName={boardName} userName={userName} onLogout={onLogout} onManageBilling={openBillingPortal} />
        </div>
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "44px 22px 64px" }}>
          <div
            style={{
              textAlign: "center",
              borderBottom: `1px solid ${HAIR}`,
              paddingBottom: 26,
              marginBottom: 28,
            }}
          >
            <div
              style={{
                fontSize: 12,
                letterSpacing: "0.42em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 600,
              }}
            >
              Hey Sova
            </div>
            <h1
              data-testid="board-title"
              onClick={() => {
                setRenameDraft(boardName);
                setRenaming(true);
              }}
              style={{
                fontFamily: DISPLAY,
                fontSize: 42,
                margin: "12px 0 6px",
                fontWeight: 400,
                lineHeight: 1.05,
                cursor: "pointer",
              }}
              title="Click to rename"
            >
              {renaming ? (
                <input
                  data-testid="board-rename-input"
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveBoardRename();
                    if (e.key === "Escape") setRenaming(false);
                  }}
                  onBlur={saveBoardRename}
                  autoFocus
                  maxLength={120}
                  style={{
                    fontFamily: DISPLAY,
                    fontSize: 42,
                    fontWeight: 400,
                    lineHeight: 1.05,
                    color: NAVY,
                    background: "transparent",
                    border: "none",
                    borderBottom: `2px solid ${ROSE}`,
                    outline: "none",
                    textAlign: "center",
                    width: "100%",
                    maxWidth: 540,
                  }}
                />
              ) : (
                boardName
              )}
            </h1>
            <p
              style={{
                fontSize: 15,
                color: "rgba(0,0,0,0.7)",
                margin: 0,
                fontStyle: "normal",
                fontFamily: DISPLAY,
              }}
            >
              {userName ? `Welcome back, ${userName}. ` : ""}Sixteen advisors, on call anytime.
            </p>
          </div>

          {/* Boardroom CTA */}
          <button
            className="ea-convene-cta"
            data-testid="open-boardroom-btn"
            onClick={() => setView("boardroom")}
            style={{
              width: "100%",
              cursor: "pointer",
              border: "none",
              background: NAVY,
              color: CREAM,
              borderRadius: 14,
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 12,
                background: ROSE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Users size={22} color={CREAM} />
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontFamily: DISPLAY, fontSize: 19 }}>Convene the Boardroom</div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>
                Put one question to the whole room and hear every take.
              </div>
            </div>
          </button>

          <button
            className="ea-convene-cta"
            data-testid="open-plan-btn"
            onClick={openPlan}
            style={{
              width: "100%",
              cursor: "pointer",
              border: `1px solid ${reminders.overdue > 0 ? ROSE : HAIR}`,
              background: PAPER,
              color: NAVY,
              borderRadius: 14,
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: reminders.overdue > 0 ? ROSE : NAVY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ListChecks size={18} color={CREAM} />
            </div>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontFamily: DISPLAY, fontSize: 17 }}>Your action plan</div>
              <div style={{ fontSize: 12.5, color: "rgba(0,0,0,0.65)" }}>
                {reminders.open === 0
                  ? "No open milestones yet — convene the room to set some."
                  : reminders.overdue > 0
                  ? `${reminders.overdue} overdue · ${reminders.due_this_week} due this week`
                  : `${reminders.open} open · ${reminders.due_this_week} due this week`}
              </div>
            </div>
            {(reminders.overdue > 0 || reminders.due_today > 0) && (
              <span
                data-testid="reminders-pill"
                style={{
                  background: ROSE,
                  color: CREAM,
                  borderRadius: 0,
                  padding: "3px 9px",
                  fontSize: 11,
                  fontFamily: BODY,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                }}
              >
                {reminders.overdue + reminders.due_today}
              </span>
            )}
          </button>

          <button
            className="ea-convene-cta"
            data-testid="open-notes-btn"
            onClick={openNotes}
            style={{
              width: "100%",
              cursor: "pointer",
              border: `1px solid ${HAIR}`,
              background: PAPER,
              color: NAVY,
              borderRadius: 14,
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: NAVY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <NotebookPen size={18} color={CREAM} />
            </div>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontFamily: DISPLAY, fontSize: 17 }}>Founder notes</div>
              <div style={{ fontSize: 12.5, color: "rgba(0,0,0,0.65)" }}>
                Your private journal — late-night thoughts, hypotheses, raw notes.
              </div>
            </div>
          </button>

          <button
            className="ea-convene-cta"
            data-testid="open-briefing-btn"
            onClick={openBriefing}
            style={{
              width: "100%",
              cursor: "pointer",
              border: `1px solid ${ROSE}`,
              background: PAPER,
              color: NAVY,
              borderRadius: 14,
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: ROSE,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Sparkles size={18} color={CREAM} />
            </div>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontFamily: DISPLAY, fontSize: 17 }}>The Monday Briefing</div>
              <div style={{ fontSize: 12.5, color: "rgba(0,0,0,0.65)" }}>
                Your Chief of Staff's read on the week — top 3 moves, open loops, kill list.
              </div>
            </div>
          </button>

          <button
            className="ea-convene-cta"
            data-testid="open-compare-btn"
            onClick={() => {
              setCompareResult(null);
              setView("compare");
            }}
            style={{
              width: "100%",
              cursor: "pointer",
              border: `1px solid ${HAIR}`,
              background: PAPER,
              color: NAVY,
              borderRadius: 14,
              padding: "14px 18px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: NAVY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <GitCompare size={18} color={CREAM} />
            </div>
            <div style={{ textAlign: "left", flex: 1 }}>
              <div style={{ fontFamily: DISPLAY, fontSize: 17 }}>Compare two takes</div>
              <div style={{ fontSize: 12.5, color: "rgba(0,0,0,0.65)" }}>
                Pick two advisors, ask one question, read them side by side.
              </div>
            </div>
          </button>

          <button
            data-testid="open-history-btn"
            onClick={openHistory}
            style={{
              width: "100%",
              cursor: "pointer",
              border: `1px solid ${HAIR}`,
              background: PAPER,
              color: NAVY,
              borderRadius: 14,
              padding: "12px 18px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 30,
            }}
          >
            <History size={18} color={ROSE} />
            <span style={{ fontFamily: DISPLAY, fontSize: 15 }}>Boardroom history</span>
          </button>

          {/* Group filter chips */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 7,
              marginBottom: 22,
              justifyContent: "center",
            }}
            data-testid="roster-filter"
          >
            {["All", ...groups].map((g) => {
              const on = filterGroup === g;
              return (
                <button
                  key={g}
                  data-testid={`filter-${g.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                  onClick={() => setFilterGroup(g)}
                  style={{
                    cursor: "pointer",
                    border: `1px solid ${on ? NAVY : HAIR}`,
                    background: on ? NAVY : PAPER,
                    color: on ? CREAM : "rgba(0,0,0,0.7)",
                    borderRadius: 20,
                    padding: "6px 14px",
                    fontFamily: BODY,
                    fontSize: 12.5,
                    letterSpacing: "0.02em",
                  }}
                >
                  {g}
                </button>
              );
            })}
          </div>

          {groups
            .filter((g) => filterGroup === "All" || filterGroup === g)
            .map((g) => (
            <div key={g} style={{ marginBottom: 34 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                <span
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.3em",
                    textTransform: "uppercase",
                    color: NAVY,
                    fontWeight: 700,
                  }}
                >
                  {g}
                </span>
                <span style={{ flex: 1, height: 1, background: HAIR }} />
              </div>
              <div style={{ display: "grid", gap: 12 }}>
                {team
                  .filter((m) => m.group === g)
                  .map((m, i) => (
                    <button
                      key={m.id}
                      className="ea-card"
                      data-testid={`open-chat-${m.id}`}
                      onClick={() => openChat(m.id)}
                      style={{
                        animationDelay: `${i * 50}ms`,
                        textAlign: "left",
                        cursor: "pointer",
                        border: `1px solid ${HAIR}`,
                        background: PAPER,
                        borderRadius: 14,
                        padding: "15px 18px",
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                      }}
                    >
                      <Avatar member={m} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: DISPLAY, fontSize: 19, lineHeight: 1.15 }}>
                          {m.role}
                        </div>
                        {m.inspired_by && (
                          <div
                            data-testid={`inspired-by-${m.id}`}
                            style={{
                              fontFamily: DISPLAY,
                              fontStyle: "normal",
                              fontSize: 12.5,
                              color: "rgba(0,0,0,0.55)",
                              marginBottom: 4,
                            }}
                          >
                            in the school of {m.inspired_by}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 13.5,
                            color: "rgba(0,0,0,0.72)",
                            lineHeight: 1.35,
                          }}
                        >
                          {m.beat}
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          ))}
          <p
            style={{
              textAlign: "center",
              fontSize: 11.5,
              color: "rgba(0,0,0,0.45)",
              marginTop: 4,
            }}
          >
            Every advisor is briefed on your business. Conversations are saved so you can pick up
            where you left off.
          </p>
        </div>
      </div>
    );
  }

  // ========== BOARDROOM HISTORY ==========
  if (view === "history") {
    return (
      <div style={{ ...wrap, display: "flex", flexDirection: "column", height: "100vh" }} data-testid="history-view">
        <div
          style={{
            borderBottom: `1px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            data-testid="back-from-history"
            onClick={() => setView("roster")}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: NAVY,
              padding: 4,
              display: "flex",
            }}
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 18, lineHeight: 1.1 }}>Boardroom history</div>
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 600,
              }}
            >
              {boardSessions.length} session{boardSessions.length === 1 ? "" : "s"}
            </div>
          </div>
          <RoomBadge boardName={boardName} userName={userName} onLogout={onLogout} onManageBilling={openBillingPortal} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 16px" }}>
          <div style={{ maxWidth: 640, margin: "0 auto", display: "grid", gap: 12 }}>
            {boardSessions.length === 0 && (
              <p
                style={{
                  fontFamily: DISPLAY,
                  fontStyle: "normal",
                  fontSize: 16,
                  color: "rgba(0,0,0,0.6)",
                  lineHeight: 1.5,
                }}
              >
                No boardroom sessions yet. Convene the room and the transcripts will land here.
              </p>
            )}
            {boardSessions.map((s) => (
              <button
                key={s.id}
                data-testid={`history-item-${s.id}`}
                onClick={() => loadSession(s.id)}
                className="ea-card"
                style={{
                  textAlign: "left",
                  cursor: "pointer",
                  border: `1px solid ${HAIR}`,
                  background: PAPER,
                  borderRadius: 14,
                  padding: "14px 16px",
                }}
              >
                <div
                  style={{
                    fontSize: 10.5,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: ROSE,
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  {new Date(s.created_at).toLocaleString()} · {s.panel.length} in the room
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY,
                    fontSize: 17,
                    lineHeight: 1.3,
                    color: NAVY,
                  }}
                >
                  {s.question}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ========== BOARDROOM ==========
  if (view === "boardroom") {
    const activeSession = activeSessionId
      ? {
          id: activeSessionId,
          question: boardQuestion,
          panel,
          responses: panel
            .filter((id) => boardResponses[id])
            .map((id) => ({
              member_id: id,
              role: teamById[id]?.role,
              text: boardResponses[id].text,
            })),
          created_at: new Date().toISOString(),
          memo: memo || undefined,
        }
      : null;
    const allDone =
      activeSessionId &&
      panel.length > 0 &&
      panel.every((id) => boardResponses[id]?.status === "done");

    return (
      <div
        style={{ ...wrap, display: "flex", flexDirection: "column", height: "100vh" }}
        data-testid="boardroom-view"
      >
        <div
          style={{
            borderBottom: `1px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            data-testid="back-from-boardroom"
            onClick={() => setView("roster")}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: NAVY,
              padding: 4,
              display: "flex",
            }}
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 18, lineHeight: 1.1 }}>The Boardroom</div>
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 600,
              }}
            >
              {panel.length} in the room
            </div>
          </div>
          <RoomBadge boardName={boardName} userName={userName} />
          {activeSession && activeSession.responses.length > 0 && (
            <button
              data-testid="export-boardroom"
              onClick={() =>
                downloadText(
                  `boardroom-${activeSession.id.slice(0, 8)}.md`,
                  boardroomToMarkdown(activeSession, teamById)
                )
              }
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "rgba(0,0,0,0.7)",
                padding: 4,
                display: "flex",
              }}
              aria-label="Export"
              title="Export as markdown"
            >
              <Download size={18} />
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 16px" }}>
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            {/* Panel picker */}
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              Who's in the room?
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 22 }}>
              {team.map((m) => {
                const on = panel.includes(m.id);
                return (
                  <button
                    key={m.id}
                    data-testid={`panel-toggle-${m.id}`}
                    onClick={() => togglePanel(m.id)}
                    style={{
                      cursor: "pointer",
                      border: `1px solid ${on ? NAVY : HAIR}`,
                      background: on ? NAVY : PAPER,
                      color: on ? CREAM : "rgba(0,0,0,0.7)",
                      borderRadius: 20,
                      padding: "6px 12px",
                      fontFamily: BODY,
                      fontSize: 12.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    {(() => {
                      const r = m.role;
                      if (r === "Chief of Staff") return "Chief of Staff";
                      return r
                        .replace("Chief ", "")
                        .replace(" Officer", "")
                        .replace("Head of ", "");
                    })()}
                    {on && <X size={12} />}
                  </button>
                );
              })}
            </div>

            {/* Responses */}
            {Object.keys(boardResponses).length > 0 && (
              <div style={{ display: "grid", gap: 12, marginBottom: 16 }} data-testid="boardroom-responses">
                {panel
                  .filter((id) => boardResponses[id])
                  .map((id) => {
                    const m = teamById[id];
                    const r = boardResponses[id];
                    if (!m) return null;
                    return (
                      <div
                        key={id}
                        className="ea-msg-bubble"
                        data-testid={`board-response-${id}`}
                        style={{
                          border: `1px solid ${HAIR}`,
                          background: PAPER,
                          borderRadius: 14,
                          padding: "14px 16px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 9 }}>
                          <Avatar member={m} size={36} />
                          <div>
                            <div style={{ fontFamily: DISPLAY, fontSize: 16, lineHeight: 1.1 }}>
                              {m.role}
                            </div>
                            {m.inspired_by && (
                              <div
                                style={{
                                  fontFamily: DISPLAY,
                                  fontStyle: "normal",
                                  fontSize: 11,
                                  color: "rgba(0,0,0,0.55)",
                                  marginTop: 1,
                                }}
                              >
                                ↳ {m.inspired_by}
                              </div>
                            )}
                          </div>
                        </div>
                        {r.status === "loading" ? (
                          <div style={{ display: "flex", gap: 5, padding: "4px 0" }}>
                            {[0, 1, 2].map((d) => (
                              <span
                                key={d}
                                className="ea-dot"
                                style={{
                                  width: 6,
                                  height: 6,
                                  background: ROSE,
                                  animationDelay: `${d * 0.2}s`,
                                }}
                              />
                            ))}
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: 14.5,
                              lineHeight: 1.5,
                              whiteSpace: "pre-wrap",
                              color: r.status === "error" ? ROSE : NAVY,
                            }}
                          >
                            {r.text}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {allDone && (
              <div style={{ marginBottom: 16 }}>
                {!memo && !memoLoading && (
                  <button
                    data-testid="synthesize-memo-btn"
                    onClick={synthesizeMemo}
                    style={{
                      cursor: "pointer",
                      border: `1px solid ${NAVY}`,
                      background: NAVY,
                      color: CREAM,
                      borderRadius: 12,
                      padding: "12px 18px",
                      fontFamily: DISPLAY,
                      fontSize: 15,
                      letterSpacing: "0.02em",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: ROSE,
                        fontWeight: 700,
                      }}
                    >
                      The CEO
                    </span>
                    <span style={{ opacity: 0.55 }}>·</span>
                    <span>Synthesize the decision memo</span>
                  </button>
                )}
                {memoLoading && (
                  <div
                    data-testid="memo-loading"
                    style={{
                      border: `1px solid ${HAIR}`,
                      background: PAPER,
                      borderRadius: 14,
                      padding: "16px 18px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "flex", gap: 5 }}>
                      {[0, 1, 2].map((d) => (
                        <span
                          key={d}
                          className="ea-dot"
                          style={{
                            width: 6,
                            height: 6,
                            background: ROSE,
                            animationDelay: `${d * 0.2}s`,
                          }}
                        />
                      ))}
                    </div>
                    <div
                      style={{
                        fontFamily: DISPLAY,
                        fontStyle: "normal",
                        fontSize: 14,
                        color: "rgba(0,0,0,0.7)",
                      }}
                    >
                      The CEO is reading the room…
                    </div>
                  </div>
                )}
                {memo && (
                  <div
                    data-testid="memo-card"
                    className="ea-msg-bubble"
                    style={{
                      border: `1px solid ${NAVY}`,
                      background: PAPER,
                      borderRadius: 14,
                      padding: "18px 20px",
                      boxShadow: "0 6px 24px rgba(0,0,0,0.10)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        marginBottom: 12,
                        paddingBottom: 12,
                        borderBottom: `1px solid ${HAIR}`,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                            color: ROSE,
                            fontWeight: 700,
                          }}
                        >
                          Decision Memo
                        </div>
                        <div
                          style={{
                            fontFamily: DISPLAY,
                            fontSize: 17,
                            lineHeight: 1.15,
                            marginTop: 2,
                          }}
                        >
                          {memo.author_name || "the CEO"}
                        </div>
                      </div>
                      <button
                        data-testid="memo-to-plan"
                        onClick={extractFromMemo}
                        disabled={extractingFromMemo}
                        style={{
                          border: "none",
                          background: extractingFromMemo ? "rgba(0,0,0,0.4)" : NAVY,
                          color: CREAM,
                          cursor: extractingFromMemo ? "default" : "pointer",
                          padding: "6px 12px",
                          borderRadius: 10,
                          fontFamily: BODY,
                          fontSize: 12,
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          marginRight: 8,
                        }}
                        title="Extract milestones into your action plan"
                      >
                        <ListChecks size={13} />
                        {extractingFromMemo ? "Adding…" : "Add to plan"}
                      </button>
                      <button
                        data-testid="memo-regenerate"
                        onClick={() => {
                          setMemo(null);
                          synthesizeMemo();
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "rgba(0,0,0,0.55)",
                          padding: 4,
                          display: "flex",
                        }}
                        aria-label="Regenerate memo"
                        title="Regenerate"
                      >
                        <RotateCcw size={16} />
                      </button>
                    </div>
                    <div
                      style={{
                        fontSize: 14.5,
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        color: memo.error ? ROSE : NAVY,
                      }}
                    >
                      {memo.text}
                    </div>
                  </div>
                )}
              </div>
            )}

            {Object.keys(boardResponses).length === 0 && (
              <p
                style={{
                  fontFamily: DISPLAY,
                  fontStyle: "normal",
                  fontSize: 16,
                  color: "rgba(0,0,0,0.6)",
                  lineHeight: 1.5,
                }}
              >
                Pose a strategic question and everyone in the room answers from their own lens —
                useful when a decision touches money, ops, product, and brand at once.
              </p>
            )}
          </div>
        </div>

        {/* Composer */}
        <div
          style={{
            borderTop: `1px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              maxWidth: 640,
              margin: "0 auto",
              display: "flex",
              gap: 9,
              alignItems: "flex-end",
            }}
          >
            <textarea
              data-testid="boardroom-input"
              className="ea-textarea"
              value={boardQuestion}
              onChange={(e) => setBoardQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  convene();
                }
              }}
              placeholder="Ask the room a question…"
              rows={1}
              maxLength={8000}
              style={{
                flex: 1,
                resize: "none",
                border: `1px solid ${HAIR}`,
                borderRadius: 12,
                padding: "11px 14px",
                fontFamily: BODY,
                fontSize: 15,
                color: NAVY,
                background: CREAM,
                outline: "none",
                maxHeight: 120,
                lineHeight: 1.4,
              }}
            />
            <button
              data-testid="convene-btn"
              onClick={convene}
              disabled={boardConvening || !boardQuestion.trim() || panel.length === 0}
              style={{
                border: "none",
                cursor:
                  boardConvening || !boardQuestion.trim() || panel.length === 0
                    ? "default"
                    : "pointer",
                background:
                  boardConvening || !boardQuestion.trim() || panel.length === 0
                    ? "#CCCCCC"
                    : ROSE,
                color: CREAM,
                borderRadius: 12,
                width: 46,
                height: 46,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-label="Convene"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========== COMPARE ==========
  if (view === "compare") {
    const aMember = compareA ? teamById[compareA] : null;
    const bMember = compareB ? teamById[compareB] : null;
    return (
      <div
        style={{ ...wrap, display: "flex", flexDirection: "column", height: "100vh" }}
        data-testid="compare-view"
      >
        <div
          style={{
            borderBottom: `1px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            data-testid="back-from-compare"
            onClick={() => setView("roster")}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: NAVY,
              padding: 4,
              display: "flex",
            }}
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 18, lineHeight: 1.1 }}>Compare two takes</div>
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 600,
              }}
            >
              Two advisors · one question
            </div>
          </div>
          <RoomBadge boardName={boardName} userName={userName} />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 16px" }}>
          <div style={{ maxWidth: 940, margin: "0 auto" }}>
            {/* Picker */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 22 }}>
              {[
                { side: "A", value: compareA, setValue: setCompareA, other: compareB },
                { side: "B", value: compareB, setValue: setCompareB, other: compareA },
              ].map(({ side, value, setValue, other }) => (
                <div key={side}>
                  <div
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      color: ROSE,
                      fontWeight: 700,
                      marginBottom: 6,
                    }}
                  >
                    Advisor {side}
                  </div>
                  <select
                    data-testid={`compare-select-${side.toLowerCase()}`}
                    value={value || ""}
                    onChange={(e) => setValue(e.target.value || null)}
                    style={{
                      width: "100%",
                      border: `1px solid ${HAIR}`,
                      borderRadius: 12,
                      padding: "11px 14px",
                      fontFamily: BODY,
                      fontSize: 14,
                      color: NAVY,
                      background: PAPER,
                      outline: "none",
                      appearance: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="">Choose an advisor…</option>
                    {team
                      .filter((m) => m.id !== other)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          The {m.role}
                        </option>
                      ))}
                  </select>
                  {value && teamById[value]?.inspired_by && (
                    <div
                      style={{
                        fontFamily: DISPLAY,
                        fontStyle: "normal",
                        fontSize: 12.5,
                        color: "rgba(0,0,0,0.55)",
                        marginTop: 6,
                      }}
                    >
                      in the school of {teamById[value].inspired_by}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Result */}
            {!compareResult && (
              <p
                style={{
                  fontFamily: DISPLAY,
                  fontStyle: "normal",
                  fontSize: 16,
                  color: "rgba(0,0,0,0.6)",
                  lineHeight: 1.5,
                }}
              >
                Pick two advisors and pose a narrow, specific question. Best for decisions where you
                want a tight, side-by-side argument — not a full boardroom.
              </p>
            )}

            {compareResult && (
              <div data-testid="compare-result">
                <div
                  style={{
                    fontFamily: DISPLAY,
                    fontSize: 18,
                    fontStyle: "normal",
                    color: NAVY,
                    lineHeight: 1.4,
                    marginBottom: 16,
                    paddingBottom: 14,
                    borderBottom: `1px solid ${HAIR}`,
                  }}
                >
                  "{compareResult.question}"
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 14,
                  }}
                >
                  {["a", "b"].map((side) => {
                    const r = compareResult[side];
                    const m = side === "a" ? aMember : bMember;
                    return (
                      <div
                        key={side}
                        className="ea-msg-bubble"
                        data-testid={`compare-card-${side}`}
                        style={{
                          border: `1px solid ${HAIR}`,
                          background: PAPER,
                          borderRadius: 14,
                          padding: "16px 18px",
                        }}
                      >
                        {m && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 11,
                              marginBottom: 11,
                              paddingBottom: 11,
                              borderBottom: `1px solid ${HAIR}`,
                            }}
                          >
                            <Avatar member={m} size={36} />
                            <div>
                              <div style={{ fontFamily: DISPLAY, fontSize: 16, lineHeight: 1.1 }}>
                                {m.role}
                              </div>
                              {m.inspired_by && (
                                <div
                                  style={{
                                    fontFamily: DISPLAY,
                                    fontStyle: "normal",
                                    fontSize: 11,
                                    color: "rgba(0,0,0,0.55)",
                                  }}
                                >
                                  ↳ {m.inspired_by}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {compareResult.loading || compareLoading ? (
                          <div style={{ display: "flex", gap: 5, padding: "4px 0" }}>
                            {[0, 1, 2].map((d) => (
                              <span
                                key={d}
                                className="ea-dot"
                                style={{
                                  width: 6,
                                  height: 6,
                                  background: ROSE,
                                  animationDelay: `${d * 0.2}s`,
                                }}
                              />
                            ))}
                          </div>
                        ) : compareResult.error ? (
                          <div style={{ color: ROSE, fontSize: 14 }}>Connection error.</div>
                        ) : r ? (
                          <div
                            style={{
                              fontSize: 14.5,
                              lineHeight: 1.55,
                              whiteSpace: "pre-wrap",
                              color: r.status === "error" ? ROSE : NAVY,
                            }}
                          >
                            {r.text}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Composer */}
        <div
          style={{
            borderTop: `1px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              maxWidth: 940,
              margin: "0 auto",
              display: "flex",
              gap: 9,
              alignItems: "flex-end",
            }}
          >
            <textarea
              data-testid="compare-input"
              className="ea-textarea"
              value={compareQuestion}
              onChange={(e) => setCompareQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  runCompare();
                }
              }}
              placeholder="Ask both advisors a narrow question…"
              rows={1}
              maxLength={8000}
              style={{
                flex: 1,
                resize: "none",
                border: `1px solid ${HAIR}`,
                borderRadius: 12,
                padding: "11px 14px",
                fontFamily: BODY,
                fontSize: 15,
                color: NAVY,
                background: CREAM,
                outline: "none",
                maxHeight: 120,
                lineHeight: 1.4,
              }}
            />
            <button
              data-testid="compare-run-btn"
              onClick={runCompare}
              disabled={
                compareLoading ||
                !compareQuestion.trim() ||
                !compareA ||
                !compareB ||
                compareA === compareB
              }
              style={{
                border: "none",
                cursor:
                  compareLoading || !compareQuestion.trim() || !compareA || !compareB || compareA === compareB
                    ? "default"
                    : "pointer",
                background:
                  compareLoading || !compareQuestion.trim() || !compareA || !compareB || compareA === compareB
                    ? "#CCCCCC"
                    : ROSE,
                color: CREAM,
                borderRadius: 12,
                width: 46,
                height: 46,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-label="Compare"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========== BRIEFING ==========
  if (view === "briefing") {
    return (
      <div
        style={{ ...wrap, display: "flex", flexDirection: "column", height: "100vh" }}
        data-testid="briefing-view"
      >
        <div
          style={{
            borderBottom: `1px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            data-testid="back-from-briefing"
            onClick={() => setView("roster")}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: NAVY,
              padding: 4,
              display: "flex",
            }}
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 18, lineHeight: 1.1 }}>
              The Monday Briefing
            </div>
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 600,
              }}
            >
              {briefing
                ? `${new Date(briefing.created_at).toLocaleDateString()} · ${briefing.author_name}`
                : "The Chief of Staff · in the school of Keith Rabois"}
            </div>
          </div>
          <RoomBadge boardName={boardName} userName={userName} />
          {briefing && (
            <button
              data-testid="briefing-export"
              onClick={() =>
                downloadText(
                  `monday-brief-${briefing.week_of || "latest"}.md`,
                  `# Monday Briefing — ${new Date(briefing.created_at).toLocaleString()}\n_${briefing.author_name}_\n\n${briefing.text}`
                )
              }
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "rgba(0,0,0,0.55)",
                padding: 4,
                display: "flex",
              }}
              aria-label="Export"
              title="Export as markdown"
            >
              <Download size={18} />
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "22px 16px" }}>
          <div style={{ maxWidth: 680, margin: "0 auto" }}>
            {!briefing && !briefingLoading && (
              <div style={{ textAlign: "center", paddingTop: 30 }}>
                <p
                  style={{
                    fontFamily: DISPLAY,
                    fontStyle: "normal",
                    fontSize: 17,
                    color: "rgba(0,0,0,0.7)",
                    lineHeight: 1.5,
                    marginBottom: 26,
                  }}
                >
                  Your Chief of Staff reads the last seven days of boardroom sessions, your active 1:1 threads,
                  and the standing notes you've left for advisors — and returns the three highest-leverage
                  moves for the week.
                </p>
                <button
                  data-testid="briefing-generate-btn"
                  onClick={generateBriefing}
                  style={{
                    cursor: "pointer",
                    border: "none",
                    background: NAVY,
                    color: CREAM,
                    borderRadius: 12,
                    padding: "13px 22px",
                    fontFamily: DISPLAY,
                    fontSize: 16,
                    letterSpacing: "0.02em",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <Sparkles size={18} color={ROSE} />
                  Generate this week's brief
                </button>
              </div>
            )}

            {briefingLoading && (
              <div
                data-testid="briefing-loading"
                style={{
                  border: `1px solid ${HAIR}`,
                  background: PAPER,
                  borderRadius: 14,
                  padding: "20px 22px",
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <div style={{ display: "flex", gap: 5 }}>
                  {[0, 1, 2].map((d) => (
                    <span
                      key={d}
                      className="ea-dot"
                      style={{
                        width: 7,
                        height: 7,
                        background: ROSE,
                        animationDelay: `${d * 0.2}s`,
                      }}
                    />
                  ))}
                </div>
                <div
                  style={{
                    fontFamily: DISPLAY,
                    fontStyle: "normal",
                    fontSize: 15,
                    color: "rgba(0,0,0,0.7)",
                  }}
                >
                  The Chief of Staff is reading the last seven days…
                </div>
              </div>
            )}

            {briefing && !briefingLoading && (
              <div
                data-testid="briefing-card"
                className="ea-msg-bubble"
                style={{
                  border: `1px solid ${NAVY}`,
                  background: PAPER,
                  borderRadius: 14,
                  padding: "22px 26px",
                  boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: ROSE,
                    fontWeight: 700,
                    marginBottom: 14,
                  }}
                >
                  Week of {briefing.week_of || new Date(briefing.created_at).toLocaleDateString()}
                </div>
                <div
                  style={{
                    fontSize: 15,
                    lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                    color: NAVY,
                  }}
                >
                  {briefing.text}
                </div>
                <div
                  style={{
                    marginTop: 18,
                    paddingTop: 14,
                    borderTop: `1px solid ${HAIR}`,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      fontFamily: DISPLAY,
                      fontStyle: "normal",
                      fontSize: 13,
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    — {briefing.author_name}
                    {briefing.author_inspired_by ? ` · in the school of ${briefing.author_inspired_by}` : ""}
                  </div>
                  <button
                    data-testid="briefing-regenerate"
                    onClick={generateBriefing}
                    style={{
                      border: `1px solid ${HAIR}`,
                      background: "transparent",
                      cursor: "pointer",
                      color: NAVY,
                      padding: "7px 12px",
                      borderRadius: 10,
                      fontFamily: BODY,
                      fontSize: 12.5,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <RotateCcw size={13} />
                    Regenerate
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ========== PLAN ==========
  if (view === "plan") {
    const milestones = planMilestones;
    const open = openMilestones;
    const done = doneMilestones;    const todayIso = new Date().toISOString().slice(0, 10);
    const dueClass = (m) => {
      if (!m.due_date) return null;
      if (m.due_date < todayIso) return "overdue";
      if (m.due_date === todayIso) return "today";
      return "future";
    };
    return (
      <div
        style={{ ...wrap, display: "flex", flexDirection: "column", height: "100vh" }}
        data-testid="plan-view"
      >
        <div
          style={{
            borderBottom: `1px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            data-testid="back-from-plan"
            onClick={() => setView("roster")}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: NAVY,
              padding: 4,
              display: "flex",
            }}
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 18, lineHeight: 1.1 }}>
              Your action plan
            </div>
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 600,
              }}
            >
              {reminders.overdue > 0
                ? `${reminders.overdue} overdue · ${reminders.due_this_week} due this week`
                : `${reminders.open} open · ${reminders.due_this_week} due this week`}
            </div>
          </div>
          <button
            data-testid="share-plan-btn"
            onClick={openShareModal}
            aria-label="Share plan"
            style={{
              border: `2px solid ${HAIR}`,
              background: PAPER,
              color: NAVY,
              cursor: "pointer",
              padding: "8px 12px",
              marginRight: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: BODY,
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            <Share2 size={14} /> Share
          </button>
          <BellMenu
            reminders={reminders}
            sprint={plan?.sprint}
            open={bellOpen}
            onToggle={() => setBellOpen((b) => !b)}
            onOpenPlan={() => {
              /* already on plan */
            }}
          />
          <RoomBadge boardName={boardName} userName={userName} />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "18px 16px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            {/* Progress + Sprint */}
            {plan && (
              <div
                style={{
                  border: `2px solid ${HAIR}`,
                  background: PAPER,
                  padding: "18px 20px",
                  marginBottom: 18,
                }}
                data-testid="plan-progress-card"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      fontFamily: DISPLAY,
                      fontSize: 24,
                      lineHeight: 0.95,
                      textTransform: "uppercase",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {plan.progress?.percent ?? 0}% complete
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      fontWeight: 800,
                      color: NAVY,
                    }}
                  >
                    {plan.progress?.done ?? 0} / {plan.progress?.total ?? 0} done
                  </div>
                </div>
                <div
                  style={{
                    height: 10,
                    background: "#FFFFFF",
                    border: `2px solid ${HAIR}`,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      width: `${plan.progress?.percent ?? 0}%`,
                      height: "100%",
                      background: NAVY,
                    }}
                  />
                </div>
                {/* This week sprint */}
                {plan.sprint && (plan.sprint.overdue.length + plan.sprint.this_week.length) > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${HAIR}` }}>
                    <div
                      style={{
                        fontSize: 10,
                        letterSpacing: "0.2em",
                        textTransform: "uppercase",
                        fontWeight: 800,
                        marginBottom: 8,
                      }}
                    >
                      This sprint · {plan.sprint.week_start} → {plan.sprint.week_end}
                    </div>
                    {plan.sprint.overdue.map((m) => (
                      <div
                        key={m.id}
                        style={{
                          fontSize: 13,
                          padding: "4px 0",
                          fontWeight: 600,
                          color: NAVY,
                        }}
                      >
                        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", marginRight: 8, textTransform: "uppercase" }}>
                          Overdue ({m.due_date})
                        </span>
                        {m.title}
                      </div>
                    ))}
                    {plan.sprint.this_week.map((m) => (
                      <div
                        key={m.id}
                        style={{
                          fontSize: 13,
                          padding: "4px 0",
                          fontWeight: 600,
                          color: NAVY,
                        }}
                      >
                        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", marginRight: 8, textTransform: "uppercase", color: ROSE }}>
                          Due {m.due_date}
                        </span>
                        {m.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Add milestone form */}
            <div
              style={{
                border: `1px solid ${HAIR}`,
                background: PAPER,
                borderRadius: 14,
                padding: "14px 16px",
                marginBottom: 22,
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: ROSE,
                  fontWeight: 700,
                  marginBottom: 10,
                }}
              >
                Add a milestone
              </div>
              <input
                data-testid="milestone-title"
                value={newMilestone.title}
                onChange={(e) => setNewMilestone((m) => ({ ...m, title: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addMilestone();
                  }
                }}
                placeholder="e.g. Finalize bull/base/bear model"
                maxLength={280}
                style={{
                  width: "100%",
                  border: `1px solid ${HAIR}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontFamily: BODY,
                  fontSize: 14,
                  color: NAVY,
                  background: CREAM,
                  outline: "none",
                  marginBottom: 10,
                  boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <select
                  data-testid="milestone-owner"
                  value={newMilestone.owner_role}
                  onChange={(e) => setNewMilestone((m) => ({ ...m, owner_role: e.target.value }))}
                  style={{
                    flex: 1,
                    minWidth: 180,
                    border: `1px solid ${HAIR}`,
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontFamily: BODY,
                    fontSize: 13.5,
                    color: NAVY,
                    background: CREAM,
                    outline: "none",
                    cursor: "pointer",
                  }}
                >
                  <option value="">Owner: pick an advisor (optional)</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.role}>
                      The {m.role}
                    </option>
                  ))}
                </select>
                <input
                  data-testid="milestone-due"
                  type="date"
                  value={newMilestone.due_date}
                  onChange={(e) => setNewMilestone((m) => ({ ...m, due_date: e.target.value }))}
                  style={{
                    border: `1px solid ${HAIR}`,
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontFamily: BODY,
                    fontSize: 13.5,
                    color: NAVY,
                    background: CREAM,
                    outline: "none",
                  }}
                />
                <button
                  data-testid="milestone-add-btn"
                  onClick={addMilestone}
                  disabled={!newMilestone.title.trim() || milestoneSaving}
                  style={{
                    border: "none",
                    cursor:
                      newMilestone.title.trim() && !milestoneSaving ? "pointer" : "default",
                    background:
                      newMilestone.title.trim() && !milestoneSaving
                        ? NAVY
                        : "rgba(0,0,0,0.35)",
                    color: CREAM,
                    borderRadius: 10,
                    padding: "10px 16px",
                    fontFamily: BODY,
                    fontSize: 13.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Plus size={14} />
                  Add
                </button>
              </div>
            </div>

            {/* Open milestones */}
            {open.length === 0 && done.length === 0 ? (
              <p
                style={{
                  fontFamily: DISPLAY,
                  fontStyle: "normal",
                  fontSize: 16,
                  color: "rgba(0,0,0,0.6)",
                  lineHeight: 1.5,
                }}
              >
                Nothing here yet. Either add a milestone above, or convene the boardroom on a decision and
                use <strong>Add to plan</strong> on the memo to auto-extract milestones.
              </p>
            ) : (
              <>
                {open.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div
                      style={{
                        fontSize: 11,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: NAVY,
                        fontWeight: 700,
                        marginBottom: 10,
                      }}
                    >
                      Open · {open.length}
                    </div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {open.map((m) => {
                        const cls = dueClass(m);
                        const stripe =
                          cls === "overdue" ? ROSE : cls === "today" ? "#d4a847" : HAIR;
                        return (
                          <div
                            key={m.id}
                            data-testid={`milestone-${m.id}`}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 10,
                              padding: "12px 14px",
                              border: `1px solid ${HAIR}`,
                              borderLeft: `3px solid ${stripe}`,
                              background: PAPER,
                              borderRadius: 11,
                            }}
                          >
                            <button
                              data-testid={`milestone-done-${m.id}`}
                              onClick={() => setMilestoneStatus(m.id, "done")}
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 6,
                                border: `1.5px solid ${NAVY}`,
                                background: "transparent",
                                cursor: "pointer",
                                flexShrink: 0,
                                marginTop: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              aria-label="Mark done"
                              title="Mark done"
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: 14.5,
                                  lineHeight: 1.4,
                                  color: NAVY,
                                  marginBottom: 3,
                                }}
                              >
                                {m.title}
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "rgba(0,0,0,0.6)",
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 10,
                                }}
                              >
                                {m.owner_role && (
                                  <span style={{ fontFamily: DISPLAY, fontStyle: "normal" }}>
                                    The {m.owner_role}
                                  </span>
                                )}
                                {m.due_date && (
                                  <span
                                    style={{
                                      color: cls === "overdue" ? ROSE : "rgba(0,0,0,0.7)",
                                      fontWeight: cls === "overdue" ? 600 : 400,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    {cls === "overdue" && <AlertTriangle size={12} />}
                                    Due {new Date(m.due_date).toLocaleDateString()}
                                  </span>
                                )}
                                {m.source && m.source.kind && (
                                  <span style={{ opacity: 0.6 }}>from {m.source.kind}</span>
                                )}
                              </div>
                            </div>
                            <button
                              data-testid={`milestone-delete-${m.id}`}
                              onClick={() => deleteMilestone(m.id)}
                              style={{
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                color: "rgba(0,0,0,0.45)",
                                padding: 4,
                                display: "flex",
                              }}
                              aria-label="Delete"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {done.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: "rgba(0,0,0,0.5)",
                        fontWeight: 700,
                        marginBottom: 10,
                      }}
                    >
                      Done · {done.length}
                    </div>
                    <div style={{ display: "grid", gap: 6 }}>
                      {done.map((m) => (
                        <div
                          key={m.id}
                          data-testid={`milestone-done-row-${m.id}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 14px",
                            border: `1px solid ${HAIR}`,
                            background: "transparent",
                            borderRadius: 11,
                            opacity: 0.7,
                          }}
                        >
                          <button
                            onClick={() => setMilestoneStatus(m.id, "open")}
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 6,
                              border: `1.5px solid ${NAVY}`,
                              background: NAVY,
                              cursor: "pointer",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            aria-label="Reopen"
                            title="Reopen"
                          >
                            <Check size={14} color={CREAM} />
                          </button>
                          <div
                            style={{
                              flex: 1,
                              fontSize: 14,
                              color: "rgba(0,0,0,0.65)",
                              textDecoration: "line-through",
                            }}
                          >
                            {m.title}
                          </div>
                          <button
                            onClick={() => deleteMilestone(m.id)}
                            style={{
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              color: "rgba(0,0,0,0.4)",
                              padding: 4,
                              display: "flex",
                            }}
                            aria-label="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Share modal */}
        {shareModalOpen && (
          <div
            data-testid="share-modal"
            onClick={() => setShareModalOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              zIndex: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: PAPER,
                color: NAVY,
                maxWidth: 540,
                width: "100%",
                border: `2px solid ${HAIR}`,
                padding: "30px 32px",
                fontFamily: BODY,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 16 }}>
                <h2
                  style={{
                    fontFamily: DISPLAY,
                    fontSize: 30,
                    margin: 0,
                    lineHeight: 0.95,
                    letterSpacing: "-0.025em",
                    textTransform: "uppercase",
                  }}
                >
                  Share your plan
                </h2>
                <button
                  data-testid="share-modal-close"
                  onClick={() => setShareModalOpen(false)}
                  style={{
                    border: `2px solid ${HAIR}`,
                    background: CREAM,
                    cursor: "pointer",
                    width: 32,
                    height: 32,
                    fontSize: 16,
                    fontWeight: 800,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <p
                style={{
                  fontSize: 14,
                  color: NAVY,
                  margin: "0 0 18px",
                  lineHeight: 1.5,
                  fontWeight: 600,
                }}
              >
                Anyone with this link can <strong>view</strong> your plan, progress, and current
                sprint — no sign-in needed. Useful for co-founders, ops leads, or investors.
                They cannot edit or comment.
              </p>

              {shareToken ? (
                <>
                  <div
                    data-testid="share-link-box"
                    style={{
                      border: `2px solid ${HAIR}`,
                      background: CREAM,
                      padding: "14px 16px",
                      marginBottom: 14,
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 13,
                      wordBreak: "break-all",
                      fontWeight: 600,
                    }}
                  >
                    {shareUrlFor(shareToken)}
                  </div>
                  <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                    <button
                      data-testid="share-copy-btn"
                      onClick={copyShareLink}
                      style={{
                        flex: 1,
                        border: `2px solid ${HAIR}`,
                        background: NAVY,
                        color: CREAM,
                        cursor: "pointer",
                        padding: "12px 16px",
                        fontFamily: BODY,
                        fontWeight: 800,
                        fontSize: 13,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <Copy size={14} /> {shareCopied ? "Copied!" : "Copy link"}
                    </button>
                    <button
                      data-testid="share-regenerate-btn"
                      onClick={regenerateShareToken}
                      disabled={shareBusy}
                      style={{
                        border: `2px solid ${HAIR}`,
                        background: PAPER,
                        color: NAVY,
                        cursor: shareBusy ? "default" : "pointer",
                        padding: "12px 14px",
                        fontFamily: BODY,
                        fontWeight: 800,
                        fontSize: 12,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                      }}
                    >
                      Regenerate
                    </button>
                  </div>
                  <button
                    data-testid="share-revoke-btn"
                    onClick={revokeShareToken}
                    disabled={shareBusy}
                    style={{
                      width: "100%",
                      border: `2px solid ${HAIR}`,
                      background: PAPER,
                      color: NAVY,
                      cursor: shareBusy ? "default" : "pointer",
                      padding: "12px 14px",
                      fontFamily: BODY,
                      fontWeight: 800,
                      fontSize: 12,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    Revoke share link
                  </button>
                </>
              ) : (
                <button
                  data-testid="share-create-btn"
                  onClick={regenerateShareToken}
                  disabled={shareBusy}
                  style={{
                    width: "100%",
                    border: `2px solid ${HAIR}`,
                    background: NAVY,
                    color: CREAM,
                    cursor: shareBusy ? "default" : "pointer",
                    padding: "14px 16px",
                    fontFamily: BODY,
                    fontWeight: 800,
                    fontSize: 13,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {shareBusy ? "Creating link…" : "Create share link"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }


  // ========== NOTES ==========
  if (view === "notes") {
    const activeNote = founderNotes.find((n) => n.id === activeNoteId);
    return (
      <div
        style={{ ...wrap, display: "flex", flexDirection: "column", height: "100vh" }}
        data-testid="notes-view"
      >
        <div
          style={{
            borderBottom: `2px solid ${HAIR}`,
            background: PAPER,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            data-testid="back-from-notes"
            onClick={() => setView("roster")}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: NAVY,
              padding: 4,
              display: "flex",
            }}
            aria-label="Back"
          >
            <ArrowLeft size={22} />
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: DISPLAY, fontSize: 18, lineHeight: 1.1, textTransform: "uppercase", letterSpacing: "-0.01em" }}>
              Founder Notes
            </div>
            <div
              style={{
                fontSize: 10.5,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: ROSE,
                fontWeight: 700,
              }}
            >
              {founderNotes.length} {founderNotes.length === 1 ? "note" : "notes"} · private to you
            </div>
          </div>
          <BellMenu
            reminders={reminders}
            sprint={plan?.sprint}
            open={bellOpen}
            onToggle={() => setBellOpen((b) => !b)}
            onOpenPlan={openPlan}
          />
          <RoomBadge boardName={boardName} userName={userName} />
        </div>

        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Sidebar */}
          <div
            style={{
              width: 260,
              borderRight: `2px solid ${HAIR}`,
              background: PAPER,
              display: "flex",
              flexDirection: "column",
              flexShrink: 0,
            }}
          >
            <button
              data-testid="new-note-btn"
              onClick={createNote}
              style={{
                margin: 12,
                padding: "12px 14px",
                border: `2px solid ${HAIR}`,
                background: NAVY,
                color: CREAM,
                cursor: "pointer",
                fontFamily: BODY,
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <Plus size={14} /> New note
            </button>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {founderNotes.length === 0 && (
                <div
                  style={{
                    padding: "20px 16px",
                    fontSize: 13,
                    color: "#666666",
                    fontWeight: 600,
                    lineHeight: 1.5,
                  }}
                >
                  No notes yet. Start your first one — your private journal sits beside the
                  executive's voice.
                </div>
              )}
              {founderNotes.map((n) => (
                <button
                  key={n.id}
                  data-testid={`note-item-${n.id}`}
                  onClick={() => selectNote(n.id)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderBottom: `1px solid ${HAIR}`,
                    background: activeNoteId === n.id ? NAVY : "transparent",
                    color: activeNoteId === n.id ? CREAM : NAVY,
                    cursor: "pointer",
                    padding: "14px 16px",
                    fontFamily: BODY,
                  }}
                >
                  <div
                    style={{
                      fontFamily: DISPLAY,
                      fontSize: 14,
                      lineHeight: 1.2,
                      textTransform: "uppercase",
                      letterSpacing: "-0.01em",
                      marginBottom: 4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {n.title || "Untitled note"}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      opacity: 0.7,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {new Date(n.updated_at).toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Editor */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              padding: "24px 32px",
              background: CREAM,
              overflowY: "auto",
            }}
          >
            {!activeNote ? (
              <div
                style={{
                  margin: "auto",
                  textAlign: "center",
                  maxWidth: 360,
                  color: "#666666",
                }}
              >
                <NotebookPen size={32} style={{ marginBottom: 16, color: NAVY }} />
                <div
                  style={{
                    fontFamily: DISPLAY,
                    fontSize: 24,
                    textTransform: "uppercase",
                    color: NAVY,
                    letterSpacing: "-0.02em",
                    marginBottom: 8,
                  }}
                >
                  Start writing.
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>
                  Hit <strong>New note</strong> to create your first founder note. These stay
                  private — your executive does not read them.
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <input
                    data-testid="note-title-input"
                    value={noteDraft.title}
                    onChange={(e) => setNoteDraft((d) => ({ ...d, title: e.target.value }))}
                    placeholder="Untitled note"
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      fontFamily: DISPLAY,
                      fontSize: 32,
                      fontWeight: 400,
                      letterSpacing: "-0.025em",
                      textTransform: "uppercase",
                      color: NAVY,
                      padding: 0,
                    }}
                  />
                  <button
                    data-testid="save-note-btn"
                    onClick={saveActiveNote}
                    disabled={noteSaving}
                    style={{
                      border: `2px solid ${HAIR}`,
                      background: noteSaving ? "#CCCCCC" : NAVY,
                      color: CREAM,
                      cursor: noteSaving ? "default" : "pointer",
                      padding: "10px 16px",
                      fontFamily: BODY,
                      fontWeight: 800,
                      fontSize: 12,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {noteSaving ? "Saving…" : "Save"}
                  </button>
                  <button
                    data-testid="delete-note-btn"
                    onClick={deleteActiveNote}
                    aria-label="Delete"
                    style={{
                      border: `2px solid ${HAIR}`,
                      background: PAPER,
                      color: NAVY,
                      cursor: "pointer",
                      padding: "10px 12px",
                      display: "inline-flex",
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <textarea
                  data-testid="note-body-input"
                  value={noteDraft.body}
                  onChange={(e) => setNoteDraft((d) => ({ ...d, body: e.target.value }))}
                  placeholder="Write whatever you're chewing on. No advisor reads this."
                  style={{
                    flex: 1,
                    minHeight: 320,
                    border: `2px solid ${HAIR}`,
                    background: PAPER,
                    color: NAVY,
                    padding: "20px 22px",
                    fontFamily: BODY,
                    fontSize: 15,
                    lineHeight: 1.6,
                    fontWeight: 500,
                    resize: "vertical",
                    outline: "none",
                  }}
                />
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 11,
                    color: "#666666",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                  }}
                >
                  Last saved {new Date(activeNote.updated_at).toLocaleString()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }


  // ========== 1:1 CHAT ==========
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: CREAM,
        fontFamily: BODY,
        color: NAVY,
      }}
      data-testid="chat-view"
    >
      <div
        style={{
          borderBottom: `1px solid ${HAIR}`,
          background: PAPER,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          data-testid="back-from-chat"
          onClick={() => setView("roster")}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: NAVY,
            padding: 4,
            display: "flex",
          }}
          aria-label="Back"
        >
          <ArrowLeft size={22} />
        </button>
        <Avatar member={active} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: DISPLAY, fontSize: 17, lineHeight: 1.1 }}>{active.role}</div>
          {active.inspired_by && (
            <div
              style={{
                fontFamily: DISPLAY,
                fontStyle: "normal",
                fontSize: 12,
                color: "rgba(0,0,0,0.55)",
                marginTop: 1,
              }}
            >
              in the school of {active.inspired_by}
            </div>
          )}
        </div>
        <RoomBadge boardName={boardName} userName={userName} />
        <button
          data-testid="edit-notes"
          onClick={startEditingNotes}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "rgba(0,0,0,0.55)",
            padding: 4,
            display: "flex",
            position: "relative",
          }}
          aria-label="Edit notes"
          title={notesByMember[activeId] ? "Edit standing notes" : "Add standing notes"}
        >
          <Pencil size={18} />
          {notesByMember[activeId] && (
            <span
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: ROSE,
              }}
            />
          )}
        </button>
        {convo.length > 0 && (
          <>
            <button
              data-testid="export-chat"
              onClick={() =>
                downloadText(
                  `${active.id}-conversation.md`,
                  conversationToMarkdown(active, convo)
                )
              }
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "rgba(0,0,0,0.55)",
                padding: 4,
                display: "flex",
              }}
              aria-label="Export"
              title="Export as markdown"
            >
              <Download size={18} />
            </button>
            <button
              data-testid="clear-chat"
              onClick={clearChat}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                color: "rgba(0,0,0,0.55)",
                padding: 4,
                display: "flex",
              }}
              aria-label="Clear"
              title="Clear conversation"
            >
              <RotateCcw size={18} />
            </button>
          </>
        )}
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "20px 16px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          {notesEditing && (
            <div
              data-testid="notes-editor"
              style={{
                border: `1px solid ${ROSE}`,
                background: PAPER,
                borderRadius: 14,
                padding: "16px 18px",
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: ROSE,
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                Standing notes for the {active.role}
              </div>
              <div
                style={{
                  fontFamily: DISPLAY,
                  fontStyle: "normal",
                  fontSize: 13,
                  color: "rgba(0,0,0,0.6)",
                  marginBottom: 10,
                  lineHeight: 1.4,
                }}
              >
                Context this advisor should remember across every conversation. Numbers,
                preferences, constraints, hard nos.
              </div>
              <textarea
                data-testid="notes-input"
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                maxLength={8000}
                rows={4}
                placeholder="e.g. We have 11 months of runway. I want to raise $1.5M on a $12M cap. No participating preferred. Lead with dollar figures."
                style={{
                  width: "100%",
                  resize: "vertical",
                  border: `1px solid ${HAIR}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontFamily: BODY,
                  fontSize: 14,
                  color: NAVY,
                  background: CREAM,
                  outline: "none",
                  lineHeight: 1.45,
                  boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                <button
                  data-testid="notes-cancel"
                  onClick={() => setNotesEditing(false)}
                  style={{
                    border: `1px solid ${HAIR}`,
                    background: "transparent",
                    cursor: "pointer",
                    color: "rgba(0,0,0,0.7)",
                    padding: "8px 14px",
                    borderRadius: 10,
                    fontFamily: BODY,
                    fontSize: 13.5,
                  }}
                >
                  Cancel
                </button>
                <button
                  data-testid="notes-save"
                  onClick={saveNotes}
                  disabled={notesSaving}
                  style={{
                    border: "none",
                    background: notesSaving ? "rgba(0,0,0,0.4)" : NAVY,
                    cursor: notesSaving ? "default" : "pointer",
                    color: CREAM,
                    padding: "8px 14px",
                    borderRadius: 10,
                    fontFamily: BODY,
                    fontSize: 13.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Save size={14} />
                  {notesSaving ? "Saving…" : "Save notes"}
                </button>
              </div>
            </div>
          )}
          {convo.length === 0 && (
            <div style={{ paddingTop: 8 }}>
              <p
                style={{
                  fontFamily: DISPLAY,
                  fontStyle: "normal",
                  fontSize: 17,
                  color: "rgba(0,0,0,0.78)",
                  marginTop: 0,
                }}
              >
                {active.beat}
              </p>
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: ROSE,
                  fontWeight: 600,
                  margin: "22px 0 10px",
                }}
              >
                Try asking
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {active.starters.map((s, i) => (
                  <button
                    key={s}
                    data-testid={`starter-${active.id}-${i}`}
                    onClick={() => send(s)}
                    style={{
                      textAlign: "left",
                      cursor: "pointer",
                      border: `1px solid ${HAIR}`,
                      background: PAPER,
                      borderRadius: 11,
                      padding: "11px 14px",
                      fontFamily: BODY,
                      fontSize: 14.5,
                      color: NAVY,
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {convo.map((m, i) => {
            const isUser = m.role === "user";
            // Chat messages don't carry server ids, but a composite of role +
            // content prefix + index is stable across the typical append-only
            // chat lifecycle and avoids the React array-index-key warning.
            const msgKey = `${m.role}-${i}-${(m.content || "").slice(0, 20)}`;
            return (
              <div
                key={msgKey}
                className="ea-msg-bubble"
                style={{
                  display: "flex",
                  justifyContent: isUser ? "flex-end" : "flex-start",
                  marginBottom: 12,
                }}
              >
                <div
                  data-testid={`msg-${isUser ? "user" : "assistant"}-${i}`}
                  style={{
                    maxWidth: "82%",
                    padding: "11px 15px",
                    borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: isUser ? NAVY : PAPER,
                    color: isUser ? CREAM : NAVY,
                    border: isUser ? "none" : `1px solid ${HAIR}`,
                    fontSize: 15,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {m.content}
                </div>
              </div>
            );
          })}
          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
              <div
                style={{
                  padding: "13px 18px",
                  borderRadius: "16px 16px 16px 4px",
                  background: PAPER,
                  border: `1px solid ${HAIR}`,
                  display: "flex",
                  gap: 5,
                }}
              >
                {[0, 1, 2].map((d) => (
                  <span
                    key={d}
                    className="ea-dot"
                    style={{
                      width: 7,
                      height: 7,
                      background: ROSE,
                      animationDelay: `${d * 0.2}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          borderTop: `1px solid ${HAIR}`,
          background: PAPER,
          padding: "12px 16px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto",
            display: "flex",
            gap: 9,
            alignItems: "flex-end",
          }}
        >
          <textarea
            data-testid="chat-input"
            className="ea-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={`Message the ${active.role}…`}
            rows={1}
            maxLength={8000}
            style={{
              flex: 1,
              resize: "none",
              border: `1px solid ${HAIR}`,
              borderRadius: 12,
              padding: "11px 14px",
              fontFamily: BODY,
              fontSize: 15,
              color: NAVY,
              background: CREAM,
              outline: "none",
              maxHeight: 120,
              lineHeight: 1.4,
            }}
          />
          <button
            data-testid="send-btn"
            onClick={() => send()}
            disabled={loading || !input.trim()}
            style={{
              border: "none",
              cursor: loading || !input.trim() ? "default" : "pointer",
              background: loading || !input.trim() ? "#CCCCCC" : ROSE,
              color: CREAM,
              borderRadius: 12,
              width: 46,
              height: 46,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background .2s ease",
            }}
            aria-label="Send"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
